// src/components/common/Layout/Header.tsx
import {useCallback, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {
  Menu,
  Bell,
  User,
  Settings,
  LogOut,
  Sun,
  Moon,
  Globe,
} from "lucide-react";
import {Menu as HeadlessMenu, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {clsx} from "clsx";
import {useLogoutMutation} from "../../../store/api/authApi";
import {logout} from "../../../store/slices/authSlice";
import {selectCurrentUser} from "../../../store/slices/authSlice";
import {useToast} from "../../../hooks/common/useToast";
import {ROUTES, MESSAGES} from "../../../utils/constants";
import {debounce} from "../../../utils";

interface HeaderProps {
  onMobileMenuToggle: () => void;
  sidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onMobileMenuToggle,
  sidebarCollapsed,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {success, error} = useToast();

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentLang, setCurrentLang] = useState<"eng" | "mm">("eng");

  // Get user from Redux store
  const user = useSelector(selectCurrentUser);

  // Logout mutation
  const [logoutMutation, {isLoading: isLogoutLoading}] = useLogoutMutation();

  const notificationCount = 3;

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle("dark");
  };

  const toggleLanguage = () => {
    setCurrentLang((prev) => (prev === "eng" ? "mm" : "eng"));
  };

  const handleLogout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
      dispatch(logout());
      success("Success", MESSAGES.SUCCESS.LOGOUT);
      navigate(ROUTES.LOGIN, {replace: true});
    } catch (err: any) {
      console.error("Logout error:", err);
      dispatch(logout());
      navigate(ROUTES.LOGIN, {replace: true});
      error("Error", err?.data?.message || "Logout failed");
    }
  }, [logoutMutation, dispatch, success, error, navigate]);

  // ✅ Debounce logout to prevent multiple calls
  const debouncedLogout = useCallback(debounce(handleLogout, 1000), [
    handleLogout,
  ]);

  const handleChangePassword = () => {
    navigate(ROUTES.CHANGE_PASSWORD);
  };

  return (
    <header
      className={clsx(
        "bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 px-4 lg:px-6 py-4 transition-all duration-300",
        "fixed top-0 right-0 z-30",
        sidebarCollapsed ? "left-0 md:left-20" : "left-0 md:left-64"
      )}
    >
      <div className="flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center">
          <button
            onClick={onMobileMenuToggle}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 md:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="hidden md:block">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Dashboard
            </h2>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            title="Change Language"
          >
            <div className="flex items-center space-x-1">
              <Globe className="w-5 h-5" />
              <span className="text-sm font-medium">
                {currentLang === "eng" ? "EN" : "MM"}
              </span>
            </div>
          </button>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            title="Toggle Dark Mode"
          >
            {isDarkMode ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>

          {/* Notifications */}
          {/* <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors relative">
            <Bell className="w-5 h-5" />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {notificationCount}
              </span>
            )}
          </button> */}

          {/* User Profile Dropdown */}
          <HeadlessMenu as="div" className="relative">
            <HeadlessMenu.Button className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name || "Admin User"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email || "admin@propertyseeker.com"}
                </div>
              </div>
            </HeadlessMenu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <HeadlessMenu.Items className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <div className="py-1">
                  {/* Profile Section */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-700">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.name || "Admin User"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.email || "admin@propertyseeker.com"}
                    </div>
                    <div className="text-xs text-primary-600 dark:text-primary-400 capitalize">
                      {user?.role || "admin"}
                    </div>
                  </div>

                  {/* Change Password */}
                  <HeadlessMenu.Item>
                    {({active}) => (
                      <button
                        onClick={handleChangePassword}
                        className={clsx(
                          "flex items-center w-full px-4 py-2 text-sm text-left",
                          active
                            ? "bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white"
                            : "text-gray-700 dark:text-gray-300"
                        )}
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Change Password
                      </button>
                    )}
                  </HeadlessMenu.Item>

                  <div className="border-t border-gray-100 dark:border-dark-700" />

                  {/* Logout */}
                  <HeadlessMenu.Item>
                    {({active}) => (
                      <button
                        onClick={handleLogout}
                        disabled={isLogoutLoading}
                        className={clsx(
                          "flex items-center w-full px-4 py-2 text-sm text-left",
                          active
                            ? "bg-gray-100 dark:bg-dark-700 text-danger-600"
                            : "text-danger-600",
                          isLogoutLoading && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        {isLogoutLoading ? "Logging out..." : "Logout"}
                      </button>
                    )}
                  </HeadlessMenu.Item>
                </div>
              </HeadlessMenu.Items>
            </Transition>
          </HeadlessMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
